import { Zdate, ZdateTime } from './common.zod';
import { ZTechlogTripCategory } from './techLog/trip.zod';
import { z } from 'zod';

// StandardizedUserRole enum schema
export const ZStandardizedUserRole = z
  .enum(['Command', 'FirstOfficer', 'FlightAttendant', 'Other'])
  .describe('Role of the crew member');

// StandardizedUser schema
export const ZStandardizedUser = z.object({
  firstName: z.string().optional().describe('First name of the crew member'),
  lastName: z.string().optional().describe('Last name of the crew member'),
  email: z.string().optional().describe('Email address of the crew member'),
  role: ZStandardizedUserRole.describe('Role of the crew member in the flight'),
});

// Fuel schema for StandardizedFlight
export const ZStandardizedFlightFuel = z.object({
  safPercentage: z
    .number()
    .optional()
    .describe('Percentage of Sustainable Aviation Fuel'),
  safUsed: z
    .boolean()
    .optional()
    .describe('Whether Sustainable Aviation Fuel was used'),
  blockFuel: z.number().optional().describe('Fuel at blocks (departure)'),
  usedFuel: z.number().optional().describe('Fuel used during the flight'),
  remainingFuel: z
    .number()
    .optional()
    .describe('Remaining fuel after the flight'),
  preUpliftFuel: z.number().optional().describe('Fuel before uplift'),
  uplift: z.number().optional().describe('Fuel uplift amount'),
});

// Passengers schema for StandardizedFlight
export const ZStandardizedFlightPassengers = z.object({
  children: z.number().optional().describe('Number of children passengers'),
  infants: z.number().optional().describe('Number of infant passengers'),
  male: z.number().optional().describe('Number of male passengers'),
  female: z.number().optional().describe('Number of female passengers'),
});

// StandardizedFlight schema
export const ZStandardizedFlight = z.object({
  originTripId: z
    .string()
    .optional()
    .describe('Original trip ID from the source system'),
  originId: z
    .string()
    .optional()
    .describe('Original ID from the source system'),
  date: Zdate.describe('Date of the flight'),
  // A standardized flight is only ever built for TechLog, and this field is
  // written straight onto the trip, so the destination's own enum is the honest
  // contract for it. Sources whose category vocabulary is wider must map onto
  // it or leave it unset in their schema.
  tripCategory: ZTechlogTripCategory.optional(),
  tripStatus: z.string().optional().describe('Status of the trip'),
  tripNumber: z.string().optional().describe('Trip number'),
  pilotInCommand: ZStandardizedUser.nullable()
    .optional()
    .describe('Pilot in command (Captain)'),
  secondInCommand: ZStandardizedUser.nullable()
    .optional()
    .describe('Second in command (First Officer)'),
  additionalCrew: z
    .array(ZStandardizedUser)
    .describe('Additional crew members'),
  departureAirportICAO: z
    .string()
    .optional()
    .describe('ICAO code of the departure airport'),
  arrivalAirportICAO: z
    .string()
    .optional()
    .describe('ICAO code of the arrival airport'),
  actualAirportICAO: z
    .string()
    .optional()
    .describe('ICAO code of the actual arrival airport'),
  aircraft: z.string().describe('Aircraft registration'),
  number: z.string().optional().describe('Flight number'),
  flightIcaoType: z
    .string()
    .optional()
    .describe('ICAO type-of-flight designator (flight plan Item 8): S/N/G/M/X'),
  tripType: z.string().optional().describe('Type of trip'),
  takeoffTime: ZdateTime.optional().describe('Time of takeoff'),
  landingTime: ZdateTime.optional().describe('Time of landing'),
  offblocksTime: ZdateTime.optional().describe(
    'Time when aircraft left the blocks',
  ),
  onblocksTime: ZdateTime.optional().describe(
    'Time when aircraft arrived at the blocks',
  ),
  landings: z.number().optional().describe('Number of landings performed'),
  maxFlightLevel: z
    .number()
    .optional()
    .describe('Maximum flight level reached'),
  totalApuHours: z
    .string()
    .optional()
    .describe('Total APU hours for the flight'),
  totalAPUCycles: z
    .number()
    .optional()
    .describe('Total APU cycles for the flight'),
  flightLog: z.string().optional().describe('Flight log information'),
  flightNumber: z.string().optional().describe('Flight number'),
  passengers: ZStandardizedFlightPassengers.optional().describe(
    'Passenger information breakdown',
  ),
  pax: z.number().optional().describe('Total number of passengers'),
  fuel: ZStandardizedFlightFuel.optional().describe('Fuel information'),
  hobbsDeparture: z
    .number()
    .optional()
    .describe('Hobbs meter reading at departure'),
  hobbsArrival: z
    .number()
    .optional()
    .describe('Hobbs meter reading at arrival'),
  deicing: z.boolean().optional().describe('Whether deicing was performed'),
  postflightCustomData: z
    .any()
    .optional()
    .describe('Custom data recorded post-flight'),
  preflightCustomData: z
    .any()
    .optional()
    .describe('Custom data recorded pre-flight'),
});
